import React, {
 useState
} from 'react';
import {
 Button
} from 'shared/components/ui/button';
import {
 Textarea
} from 'shared/components/ui/textarea';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 ArrowLeft, Sparkles, Loader2
} from 'lucide-react';
import {
 SchemaColumnDataModel
} from 'shared/api/services/BEXO/Types/Bexo.types';

interface AIGenerateSchemaProps {
    onBack: () => void;
    onResult: (tableName: string, columns: SchemaColumnDataModel[]) => void;
}

const examplePrompts = [
    'User profiles with email and roles',
    'Product catalog with categories',
    'Order tracking with status',
];

function makeColumn(
    columnName: string,
    dataType: string,
    constraints: string[] = [],
    length = 255
): SchemaColumnDataModel {
    return {
        columnName,
        dataType,
        length: dataType === 'string' ? length : 0,
        fieldsAccessBy: [],
        description: '',
        readOnly: false,
        visibleStatus: true,
        format: '',
        default: '',
        constraints,
        references: {
 type: '',
table: '',
column: '',
values: []
},
        validation: [],
    };
}

function mockAISchemaGeneration(text: string): { tableName: string; columns: SchemaColumnDataModel[] } {
    const lower = text.toLowerCase();

    if (lower.includes('user') || lower.includes('profile')) {
        return {
            tableName: 'users',
            columns: [
                makeColumn('id', 'string', ['NOT NULL', 'UNIQUE'], 36),
                makeColumn('username', 'string', ['NOT NULL', 'UNIQUE'], 50),
                makeColumn('email', 'string', ['NOT NULL', 'UNIQUE'], 100),
                makeColumn('password_hash', 'string', ['NOT NULL'], 255),
                makeColumn('role', 'string', [], 30),
                makeColumn('is_active', 'boolean', []),
                makeColumn('created_at', 'datetime', []),
                makeColumn('updated_at', 'datetime', []),
            ],
        };
    }

    if (lower.includes('product') || lower.includes('catalog')) {
        return {
            tableName: 'products',
            columns: [
                makeColumn('id', 'string', ['NOT NULL', 'UNIQUE'], 36),
                makeColumn('name', 'string', ['NOT NULL'], 200),
                makeColumn('description', 'string', [], 1000),
                makeColumn('price', 'decimal', ['NOT NULL']),
                makeColumn('category', 'string', [], 100),
                makeColumn('sku', 'string', ['UNIQUE'], 50),
                makeColumn('stock_quantity', 'integer', []),
                makeColumn('is_available', 'boolean', []),
                makeColumn('created_at', 'datetime', []),
            ],
        };
    }

    if (lower.includes('order') || lower.includes('tracking')) {
        return {
            tableName: 'orders',
            columns: [
                makeColumn('id', 'string', ['NOT NULL', 'UNIQUE'], 36),
                makeColumn('customer_id', 'string', ['NOT NULL', 'FOREIGN KEY'], 36),
                makeColumn('status', 'string', ['NOT NULL'], 30),
                makeColumn('total_amount', 'decimal', ['NOT NULL']),
                makeColumn('shipping_address', 'string', [], 500),
                makeColumn('notes', 'string', [], 1000),
                makeColumn('ordered_at', 'datetime', ['NOT NULL']),
                makeColumn('shipped_at', 'datetime', []),
                makeColumn('delivered_at', 'datetime', []),
            ],
        };
    }

    // Default generic schema
    return {
        tableName: 'my_table',
        columns: [
            makeColumn('id', 'string', ['NOT NULL', 'UNIQUE'], 36),
            makeColumn('name', 'string', ['NOT NULL'], 200),
            makeColumn('description', 'string', [], 500),
            makeColumn('status', 'string', [], 30),
            makeColumn('created_at', 'datetime', []),
            makeColumn('updated_at', 'datetime', []),
        ],
    };
}

const AIGenerateSchema: React.FC<AIGenerateSchemaProps> = ({
 onBack, onResult
}) => {
    const [description, setDescription] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const handleGenerate = () => {
        if (!description.trim()) return;
        setIsProcessing(true);
        // Simulate AI processing
        setTimeout(() => {
            const result = mockAISchemaGeneration(description);
            setIsProcessing(false);
            onResult(result.tableName, result.columns);
        }, 2000);
    };

    return (
        <div className="flex flex-col gap-8 max-w-3xl mx-auto py-8">
            <div>
                <Button variant="ghost" size="sm" onClick={onBack} className="mb-4">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <div className="flex items-center gap-2">
                    <Sparkles className="h-6 w-6" />
                    <h2 className="text-2xl font-bold">Generate Schema with AI</h2>
                </div>
                <p className="text-muted-foreground mt-1">
                    Describe the data structure you need, and we'll generate the schema for you.
                </p>
            </div>

            <div className="space-y-4">
                <Textarea
                    placeholder="Describe the schema you want to create... e.g., 'A user management table with email, username, roles, and timestamps'"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={6}
                    className="resize-none"
                />

                <div className="flex flex-wrap gap-2">
                    <p className="text-sm text-muted-foreground w-full">Try an example:</p>
                    {examplePrompts.map((prompt) => (
                        <Badge
                            key={prompt}
                            variant="outline"
                            className="cursor-pointer hover:bg-muted transition-colors py-1.5 px-3"
                            onClick={() => setDescription(prompt)}
                        >
                            {prompt}
                        </Badge>
                    ))}
                </div>
            </div>

            <Button
                onClick={handleGenerate}
                disabled={!description.trim() || isProcessing}
                className="w-full"
                size="lg"
            >
                {isProcessing ? (
                    <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating schema...
                    </>
                ) : (
                    <>
                        <Sparkles className="mr-2 h-4 w-4" />
                        Generate Schema
                    </>
                )}
            </Button>
        </div>
    );
};

export default AIGenerateSchema;
